import React, { useCallback, useEffect, useState } from "react";
import Pdf from "./Pdf";
import LoadingBoundary from "../ui_atoms/LoadingBoundary";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import { customAlphabet } from "nanoid";
import { AssetWaitingView } from "./AssetWaitingView";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import { ActionButton } from "../screens/user/ScreenHeader";
import { useAssetWaiting } from "../hooks/assets";
import Loading from "./Loading";
import VStack from "../ui_atoms/VStack";
import { StyleProp, useWindowDimensions, ViewStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";

const generateShortId = customAlphabet("0123456789", 5);

type ScreenParams = StackScreenProps<RootStackParamList>;

type PdfViewerProps = {
    assetId: string;
    filename?: string;
    waitingDescription: string;
    style?: StyleProp<ViewStyle>;
    scrollEnabled?: boolean;
};

const PdfAssetViewer = ({
    assetId,
    filename,
    waitingDescription,
    style,
    scrollEnabled,
}: PdfViewerProps) => {
    const { colors } = useTheme();
    const [isShareAvailable, setIsShareAvailable] = useState(false);
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const [pdfError, setPdfError] = useState<string | null>();
    const [isSharing, setIsSharing] = useState(false);
    const [pdfHeight, setPdfHeight] = useState<number>();
    const { width: windowWidth } = useWindowDimensions();
    useEffect(() => {
        const getIsAvailableAsync = async () => {
            setIsShareAvailable(await Sharing.isAvailableAsync());
        };
        void getIsAvailableAsync();
    }, []);
    const { waiting, error, asset, reset } = useAssetWaiting(assetId);

    const sharePdf = useCallback(() => {
        const fn = async () => {
            if (asset && asset.url) {
                try {
                    setIsSharing(true);
                    const randomFileId = generateShortId();
                    const localUri = `${FileSystem.cacheDirectory || "/"}${(
                        filename || `file-${randomFileId}`
                    ).replaceAll(" ", "_")}.pdf`;
                    const downloadRet = await FileSystem.downloadAsync(asset.url || "", localUri);
                    await Sharing.shareAsync(downloadRet.uri);
                } finally {
                    setIsSharing(false);
                }
            }
        };
        void fn();
    }, [asset, filename]);

    useEffect(() => {
        if (isShareAvailable && asset) {
            navigator.setOptions?.({
                headerRight: () => {
                    return <ActionButton icon="share" onPress={sharePdf} disabled={isSharing} />;
                },
            });
        } else {
            navigator.setOptions?.({
                headerRight: undefined,
            });
        }
    }, [asset, isShareAvailable, navigator, sharePdf, assetId, isSharing]);
    const pageSpacing = 10;

    return (
        <LoadingBoundary error={error || pdfError} clearError={reset}>
            {waiting ? (
                <VStack gap={1}>
                    <AssetWaitingView description={waitingDescription} />
                    <Loading />
                </VStack>
            ) : asset ? (
                <Pdf
                    fitPolicy={0}
                    spacing={pageSpacing}
                    trustAllCerts={false}
                    scrollEnabled={scrollEnabled}
                    source={{
                        uri: asset.url || "",
                        cache: true,
                    }}
                    onLoadComplete={(numberOfPages, path, { height, width }) => {
                        const ratio = windowWidth / width;
                        const spacing = numberOfPages > 1 ? pageSpacing * (numberOfPages - 1) : 0;
                        setPdfHeight(height * numberOfPages * ratio + spacing);
                    }}
                    onError={(error) => {
                        let errorMessage: string;
                        if (error instanceof Error) {
                            errorMessage = error.message;
                        } else {
                            errorMessage = String(error);
                        }
                        setPdfError(errorMessage);
                    }}
                    style={[
                        {
                            flex: 1,
                            flexGrow: 1,
                            backgroundColor: colors.lighterGrey,
                            width: windowWidth,
                            height: pdfHeight,
                            justifyContent: "flex-start",
                            overflow: "visible",
                        },
                        style,
                    ]}
                />
            ) : null}
        </LoadingBoundary>
    );
};

export default PdfAssetViewer;
